import React, { useEffect, useState } from 'react';
import { useAppConfigContext } from '@/context/AppConfig';
import { useAppConstantsContext } from '@/context/AppConstants';
import {
  IconButton,
  Text,
  Box,
  Grid,
  Menu,
  MenuItem,
} from '@/theme/components';
import { Icons, useTheme } from '@/theme/index';
import { DateUtil } from '@/utils/dateUtil/dateUtil';
import { NormalizedNotification } from '../sriracha/types';

/**
 * A card that displays a single notification and allows the user to mark it as read.
 *
 * @param {Object} props - The component props.
 * @param {Object} props.notification - The notification object to display.
 * @param {Function} props.onReadNotification - A function to call when the notification is marked as read.
 * @param {Function} props.onNotificationClick - A function to call when the notification is clicked.
 * @returns {JSX.Element} - The rendered component.
 *
 * @example
 * <NotificationCard
 *   notification={{ id: 1, title: 'Notification', body: 'This is a notification.' }}
 *   onReadNotification={(id) => console.log(`Notification ${id} marked as read.`)}
 *   onNotificatioonClick={(notification) => console.log(`Notification ${notification.id} clicked.`)}
 * />
 */
export function NotificationCard({
  notification,
  onReadNotification,
  onNotificationClick,
}: {
  notification: NormalizedNotification;
  onReadNotification: (id: number) => void;
  onNotificationClick: (notification: NormalizedNotification) => void;
}) {
  const {
    config: { welcomeNotification, firstUnclaimedMoneyNotification },
  } = useAppConstantsContext();

  const { trackEventGA4 } = useAppConfigContext();

  const [menuIsOpen, setMenuIsOpen] = useState(false);
  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(
    null
  );
  const theme = useTheme();
  const dateParser = new DateUtil();

  const isSuggestedReportType = notification.type === 'suggested_report';
  const isReportUpdateType = notification.type === 'report_update';
  const isWelcomeType = notification.type === 'welcome';
  const isFirstUnclaimedMoneyReminder =
    notification.type === 'first_unclaimed_money_reminder';

  const [showSuggestedReportNotification, setShowSuggesteReportNotification] =
    useState(false);

  useEffect(() => {
    setShowSuggesteReportNotification(notification.parentSource !== undefined);
  }, [notification.parentSource]);

  const handleClick = () => {
    const notificationType = {
      welcome: 'welcome_notification',
      suggested_report: 'suggested_notification',
      first_unclaimed_money_reminder: 'unclaimed_notification',
      report_update: 'monitoring_notification',
      dark_web_report: 'dark_web_notification',
      lizano_bulk_search: 'fraud_bulk_interest',
    };
    trackEventGA4({
      event_name: 'feature_Engagement',
      type:
        notificationType[notification.type as keyof typeof notificationType] ??
        'notification',
      engaged_from: 'notifications',
    });

    if (!notification.read) {
      onReadNotification(notification.id);
      notification.read = true;
    }
    onNotificationClick(notification);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      handleClick();
    }
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setMenuIsOpen(true);
  };

  const handleCloseMenu = (event: React.MouseEvent) => {
    event.stopPropagation();
    setMenuIsOpen(false);
    setAnchorEl(null);
  };

  const handleMarkAsRead = (event: React.MouseEvent) => {
    event.stopPropagation();
    if (!notification.read) {
      trackEventGA4({
        event_name: 'feature_Engagement',
        type: 'mark_as_read',
        engaged_from: 'notifications',
      });

      onReadNotification(notification.id);
      notification.read = true;
    }
    handleCloseMenu(event);
  };

  const parsedDate = (date: string) => {
    return dateParser.parseDateFromString(date, 'L/dd/yyyy');
  };

  return (
    <Grid
      container
      spacing={2}
      justifyContent="space-between"
      sx={{
        padding: theme.spacing(1.5, 0),
        cursor: 'pointer',
        marginLeft: theme.spacing(0),
        marginTop: theme.spacing(0),
      }}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      role="button"
      tabIndex={0} // Allow tab navigation
      flexWrap="nowrap"
      data-testid="notification-card"
    >
      <Box
        role="button"
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          cursor: 'pointer',
          width: '90%',
          overflow: 'hidden',
          gap: theme.spacing(1),
        }}
      >
        <Box
          sx={{
            minWidth: 12,
            minHeight: 12,
            borderRadius: '50%',
            backgroundColor: notification.read
              ? 'transparent'
              : theme.palette.primary.main,
            alignSelf: 'flex-start',
            margin: theme.spacing(0.5),
          }}
        />
        <Box
          sx={{
            flexGrow: 1,
            flexShrink: 1,
            overflow: 'hidden',
          }}
        >
          {isSuggestedReportType && (
            <div>
              {showSuggestedReportNotification ? (
                <>
                  <Text
                    variant="body1"
                    mb={1.5}
                    sx={{
                      whiteSpace: 'normal',
                      overflowWrap: 'break-word',
                      wordWrap: 'break-word',
                    }}
                  >
                    Because you searched {notification.parentSource}
                  </Text>
                  <Text variant="h4" mb={0.5} sx={{ whiteSpace: 'normal' }}>
                    {notification.body}
                  </Text>
                  <Text variant="body2" fontStyle="italic">
                    Suggested report
                  </Text>
                </>
              ) : (
                <>
                  <Text
                    variant="h4"
                    mb={0.75}
                    sx={{
                      whiteSpace: 'normal',
                      overflowWrap: 'break-word',
                      wordWrap: 'break-word',
                    }}
                  >
                    {notification.title} 1
                  </Text>
                  <Text variant="body1" sx={{ whiteSpace: 'normal' }}>
                    {notification.body}
                  </Text>
                  <Text variant="caption">
                    Date added {parsedDate(notification.created_at)}
                  </Text>
                </>
              )}
            </div>
          )}
          {isWelcomeType && (
            <>
              <Text
                variant="h4"
                mb={0.5}
                sx={{ whiteSpace: 'normal' }}
                color={theme.palette.barChart.contrastText}
              >
                {notification.title} &#127881;
              </Text>
              <Text
                variant="body1"
                mb={1.5}
                sx={{
                  whiteSpace: 'normal',
                  overflowWrap: 'break-word',
                  wordWrap: 'break-word',
                }}
              >
                {welcomeNotification?.notificationBody ?? notification.body}
              </Text>
            </>
          )}
          {isFirstUnclaimedMoneyReminder && (
            <>
              <Text
                variant="h4"
                mb={0.5}
                sx={{ whiteSpace: 'normal' }}
                color={theme.palette.barChart.contrastText}
              >
                {notification.title}
              </Text>
              <Text
                variant="body1"
                mb={1.5}
                sx={{
                  whiteSpace: 'normal',
                  overflowWrap: 'break-word',
                  wordWrap: 'break-word',
                }}
              >
                {firstUnclaimedMoneyNotification?.notificationBody ??
                  notification.body}
              </Text>
            </>
          )}
          {isReportUpdateType && (
            <>
              <Text
                variant="h4"
                mb={0.75}
                sx={{
                  whiteSpace: 'normal',
                  overflowWrap: 'break-word',
                  wordWrap: 'break-word',
                }}
              >
                {notification.title}
              </Text>
              <Text variant="body1" sx={{ whiteSpace: 'normal' }}>
                {notification.body}
              </Text>
              <Text variant="caption">
                Date added {parsedDate(notification.created_at)}
              </Text>
            </>
          )}
        </Box>
      </Box>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          alignSelf: 'baseline',
        }}
      >
        <IconButton
          onClick={handleMenuClick}
          data-testid="notification-card-options"
        >
          <Icons.MoreVert color="primary" sx={{ transform: 'rotate(90deg)' }} />
        </IconButton>

        <Menu anchorEl={anchorEl} open={menuIsOpen} onClose={handleCloseMenu}>
          <MenuItem onClick={handleMarkAsRead} role="button">
            <Icons.Check />
            <Text>Mark as read</Text>
          </MenuItem>
        </Menu>
      </Box>
    </Grid>
  );
}
